import { useState, useEffect, memo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CreditCard, Package, Calendar, DollarSign, Banknote, CheckCircle, Printer } from "lucide-react";

interface PaymentConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  totalAmount: number;
  currency: "IQD" | "USD";
  productCount: number;
  date: Date;
  withPrint?: boolean;
}

export const PaymentConfirmationDialog = memo(function PaymentConfirmationDialog({
  open,
  onOpenChange,
  onConfirm,
  totalAmount,
  currency,
  productCount,
  date,
  withPrint = false,
}: PaymentConfirmationDialogProps) {
  const [isConfirming, setIsConfirming] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // ڕیسێت کردنی state کاتێک دایەلۆگ دادەخرێت
  useEffect(() => {
    if (!open) {
      setShowSuccess(false);
      setIsConfirming(false);
    }
  }, [open]);

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm();
      setShowSuccess(true);
      // دوای 1.5 چرکە دایەلۆگ دابخە
      setTimeout(() => {
        onOpenChange(false);
      }, 1500);
    } catch {
      setIsConfirming(false);
    }
  };

  const formatDate = (date: Date) => {
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return `${year}/${month}/${day}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-w-[calc(100%-2rem)]" showCloseButton={false}>
        {showSuccess ? (
          // پیشاندانی سەرکەوتوو
          <div className="flex flex-col items-center justify-center py-8">
            <div className="h-20 w-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4 animate-in zoom-in duration-300">
              <CheckCircle className="h-12 w-12 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-xl font-bold text-green-600 dark:text-green-400 mb-2">
              سەرکەوتوو بوو!
            </h3>
            <p className="text-sm text-muted-foreground text-center">
              {withPrint ? "پارەدان تۆمارکرا و چاپ دەکرێت" : "پارەدان بە سەرکەوتوویی تۆمارکرا"}
            </p>
          </div>
        ) : (
          // فۆڕمی پشتڕاستکردنەوە
          <>
            <DialogHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  {withPrint ? (
                    <Printer className="h-6 w-6 text-primary" />
                  ) : (
                    <CreditCard className="h-6 w-6 text-primary" />
                  )}
                </div>
                <div>
                  <DialogTitle className="text-right text-lg">
                    {withPrint ? "تۆمارکردن و چاپکردنی پارەدان" : "تۆمارکردنی پارەدان"}
                  </DialogTitle>
                  <DialogDescription className="text-right text-xs">
                    دڵنیابەوە لە زانیارییەکان پێش تۆمارکردن
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            <div className="space-y-3 py-4">
              {/* Total Amount Card */}
              <div className="rounded-lg border bg-gradient-to-br from-primary/5 to-primary/10 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {currency === "IQD" ? (
                      <Banknote className="h-5 w-5 text-amber-600" />
                    ) : (
                      <DollarSign className="h-5 w-5 text-emerald-600" />
                    )}
                    <span className="text-sm text-muted-foreground">کۆی گشتی</span>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold flex items-baseline gap-1.5" dir="ltr">
                      <span>{totalAmount.toLocaleString()}</span>
                      <span className="text-base font-medium text-muted-foreground">
                        {currency === "IQD" ? "د.ع" : "$"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Details */}
              <div className="space-y-2 rounded-lg border bg-card p-3">
                <div className="flex items-center justify-between py-2 border-b border-dashed">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Package className="h-4 w-4" />
                    <span>ژمارەی کاڵاکان</span>
                  </div>
                  <span className="font-semibold">{productCount}</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    <span>بەرواری پارەدان</span>
                  </div>
                  <span className="font-medium text-sm">{formatDate(date)}</span>
                </div>
              </div>

              {withPrint && (
                <div className="rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 p-3">
                  <p className="text-xs text-blue-800 dark:text-blue-200 text-center">
                    دوای تۆمارکردن، پەنجەرەی چاپکردن دەکرێتەوە
                  </p>
                </div>
              )}
            </div>

            {/* دوگمەکان */}
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isConfirming}
                className="flex-1"
              >
                پاشگەزبوونەوە
              </Button>
              <Button
                type="button"
                onClick={handleConfirm}
                disabled={isConfirming}
                className="flex-1"
              >
                {isConfirming ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent ml-2" />
                    چاوەڕوان بە...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 ml-2" />
                    دڵنیام، تۆماری بکە
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
});
